import { isNetworkFailureError } from 'shared/utils/errorHandling';
import { FORBIDDEN, TOO_MANY_REQUESTS, UNAUTHORIZED } from 'shared/contants';

export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public responseBody?: unknown
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class RateLimitError extends Error {
  readonly status = TOO_MANY_REQUESTS;

  constructor(
    message: string,
    public urlPath: string,
    public method: string,
    public retryAfterSeconds?: number,
    public remainingMs?: number
  ) {
    super(message);
    this.name = 'RateLimitError';
  }
}

export class UnauthorizedError extends Error {
  readonly status = UNAUTHORIZED;

  constructor(
    message: string,
    public urlPath: string,
    public details?: {
      method: string;
      statusText: string;
      hasAuthHeader: boolean;
    }
  ) {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

/**
 * A request authorized by the session was about to leave without its token
 * (the session ended while it was prepared): it is not sent, so the API never
 * answers 401 to the worker's own mistake and the user is not signed out by it.
 */
export class SessionTokenMissingError extends Error {
  constructor(
    public urlPath: string,
    public method: string
  ) {
    super(`Request not sent without a session token. Path: ${urlPath}`);
    this.name = 'SessionTokenMissingError';
  }
}

export function isRateLimitError(error: unknown): error is RateLimitError {
  return error instanceof RateLimitError;
}

/**
 * The API refused the project's data: the user has no access to the project
 * (removed from it, or it is gone). A state of the account, not a failure of
 * the app; asking again changes nothing until the permissions change.
 */
export function isNoProjectAccessError(error: unknown): boolean {
  return error instanceof HttpError && isNoProjectAccessStatus(error.status);
}

/** The HTTP status of a refused project, also as a serialized error carries it (`pullFailed`). */
export function isNoProjectAccessStatus(status: unknown): boolean {
  return status === FORBIDDEN;
}

/** Whether the request never reached the server: offline, DNS, connection reset. */
export function isNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError && isNetworkFailureError(error);
}
